import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Row, Col } from 'react-bootstrap';
import { Accordion } from 'react-bootstrap-5';

import Button from 'shared/button';

import _ from 'lodash';

import Modal from 'components/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faTimes, faChevronDown } from '@fortawesome/pro-solid-svg-icons';

import FilterNumberPair from '../../search_sidebar/filter_number_pair';
import FilterDatePickerPair from '../../search_sidebar/filter_datepicker_pair';
import SidebarSearch from '../../search_sidebar/sidebar_search';

const filterBoxStyles = {
  border: '1px solid #ccc',
  padding: '0 10px',
  borderRadius: '5px',
  maxHeight: '300px',
  overflowY: 'auto',
  flexGrow: '1',
  backgroundColor: 'white'
};

export default function AllFiltersModal({
  allFilterOrder,
  filterMapping,
  startingFilters,
  show,
  onChangeFilters,
  onClearFilters,
  onCloseModal
}) {
  const FILTER_TYPES_REQUIRING_OPTIONS = ['multiselect', 'accordion', 'dropdown'];
  const [filters, setFilters] = useState(startingFilters);

  const filtersModified = useMemo(() => {
    return !_.isEqual(filters, startingFilters);
  }, [filters, startingFilters]);

  useEffect(() => {
    setFilters(startingFilters);
  }, [show]);

  function changeRangeFilter(e, minOrMax, filterKey) {
    setFilters({ ...filters, [filterKey]: { ...filters[filterKey], [minOrMax]: e.currentTarget.value } });
  }

  function changeDateFilter(date, minOrMax, filterKey) {
    setFilters({ ...filters, [filterKey]: { ...filters[filterKey], [minOrMax]: date } });
  }

  function changeMultiSelectFilters(e, filterKey, optionId) {
    const { checked } = e.target;

    if (checked) {
      setFilters({ ...filters, [filterKey]: [...filters[filterKey], optionId] });
    } else {
      setFilters({ ...filters, [filterKey]: filters[filterKey].filter(id => id !== optionId) });
    }
  }

  function changeSearchFilter(e, filterKey) {
    setFilters({ ...filters, [filterKey]: e.currentTarget.value });
  }

  function clearFilters() {
    onClearFilters();
    onCloseModal();
  }

  function saveFilters() {
    onChangeFilters(filters);
    onCloseModal();
  }

  function renderDateRangeBox(filterKey, category) {
    return (
      <FilterDatePickerPair
        filter={filters[filterKey]}
        maxPlaceholder={category.maxPlaceholder}
        minPlaceholder={category.minPlaceholder}
        name={`${filterKey}InputGroup`}
        newDesignSystem
        valueKeys={['min', 'max']}
        withIcon={category.icon}
        onChange={(date, minOrMax) => changeDateFilter(date, minOrMax, filterKey)}
      />
    );
  }

  function renderRangeBox(filterKey, category) {
    return (
      <FilterNumberPair
        filter={filters[filterKey]}
        maxPlaceholder={category.maxPlaceholder}
        minPlaceholder={category.minPlaceholder}
        name={`${filterKey}InputGroup`}
        newDesignSystem
        // FIXME: valueKeys should be [0, <integer max>] - we don't have a specific limit in the backend
        valueKeys={['min', 'max']}
        withIcon={category.icon}
        onChange={(event, minOrMax) => changeRangeFilter(event, minOrMax, filterKey)}
      />
    );
  }

  function renderFilterBox(filterKey) {
    const { options } = filterMapping[filterKey];

    return (
      <div style={filterBoxStyles}>
        {options.map(option => (
          <Checkbox
            aria-label={option.label}
            checked={filters[filterKey].includes(option.value)}
            key={option.value}
            name={`${option.label}_check`}
            onChange={e => changeMultiSelectFilters(e, filterKey, option.value)}
          >
            {option.label}
          </Checkbox>
        ))}
      </div>
    );
  }

  const renderDropdownFilterBox = (filterKey) => {
    const { options } = filterMapping[filterKey];
    const normalizedOptions = options.map(opt => ({
      ...opt,
      value: String(opt.value)
    }));
    const dropdownTitle = `All ${_.capitalize(filterKey)}s`;

    return (
      <div className="filter-accordion-container">
        <Accordion alwaysOpen className="filter-accordion">
          <Accordion.Item
            className="filter-item"
            eventKey="0"
            key="0"
          >
            <Accordion.Header>
              <div className="category-label">
                {dropdownTitle}
              </div>
              <div className="dropdown-icon">
                <FontAwesomeIcon className="arrow-icon" icon={faChevronDown}/>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {normalizedOptions.map((option) => {
                return (
                  <div className="subclass-item" key={option.value}>
                    <Checkbox
                      checked={filters[filterKey].includes(option.value)}
                      key={option.value}
                      name={`${option.label}_check`}
                      onChange={(e) => {
                        const { checked } = e.target;
                        if (checked) {
                          setFilters({
                            ...filters,
                            [filterKey]: [...filters[filterKey], option.value]
                          });
                        } else {
                          setFilters({
                            ...filters,
                            [filterKey]: filters[filterKey].filter(value => value !== option.value)
                          });
                        }
                      }}
                    >
                      {option.label}
                    </Checkbox>
                  </div>
                );
              })}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    );
  };

  function renderChildFilter(key) {
    const filterData = filterMapping[key];
    const childFilterKey = filterData.childFilter.key;

    return (
      <div className="d-flex align-items-center">
        <Checkbox
          checked={!!filters[childFilterKey]}
          name={`${childFilterKey}_check`}
          style={{ position: 'relative', bottom: '2px' }}
          onChange={() => setFilters({ ...filters, [childFilterKey]: !filters[childFilterKey] })}
        >
          <i className="text-muted" style={{ position: 'relative', top: '2px' }}>{filterData.childFilter.label}</i>
        </Checkbox>
      </div>
    );
  }

  const renderSearchFilter = (filterKey) => {
    return (
      <React.Fragment>
        <SidebarSearch
          additionalClasses="filter-search-container"
          filter={filters[filterKey]}
          name={`${filterKey}InputGroup`}
          newDesignSystem
          placeholder="Search by name, address, city, or zip"
          value={filters[filterKey]}
          onChange={e => changeSearchFilter(e, filterKey)}
        />
      </React.Fragment>
    );
  };

  const renderAccordionFilterBox = (filterKey, category) => {
    const { options } = filterMapping[filterKey];
    const childFilterKey = filterMapping[filterKey].childLabel;
    let childOptions;

    if (childFilterKey) {
      childOptions = filterMapping[childFilterKey].options;
    } else {
      childOptions = [];
    }

    return (
      <div className="filter-accordion-container">
        <Accordion alwaysOpen className="filter-accordion">
          {options.map((option, index) => {
            const childOption = childOptions[index];

            return (
              <Accordion.Item
                className="filter-item"
                eventKey={index}
                key={index}
              >
                <Accordion.Header>
                  <div className="category-label">
                    {
                      childOption &&
                      <Checkbox
                        checked={filters[filterKey].includes(option.value) ||
                          childOption.every(subclass => filters[childFilterKey].includes(subclass.value))}
                        key={index}
                        name={`${option.label}_check`}
                        onChange={(e) => {
                          const subclassValues = childOption.map(subclass => subclass.value);

                          if (e.target.checked) {
                            // Add all subclass values to the child filter
                            setFilters({
                              ...filters,
                              [filterKey]: [...filters[filterKey], option.value],
                              [childFilterKey]: [...filters[childFilterKey], ...subclassValues]
                            });
                          } else {
                            // Remove all subclass values from child filter
                            setFilters({
                              ...filters,
                              [filterKey]: filters[filterKey].filter(value => value !== option.value),
                              [childFilterKey]: filters[childFilterKey].filter(value => !subclassValues.includes(value))
                            });
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }
                    {!childOption &&
                      <Checkbox
                        checked={filters[filterKey].includes(option.value)}
                        key={index}
                        name={`${option.label}_check`}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({
                              ...filters,
                              [filterKey]: [...filters[filterKey], option.value]
                            });
                          } else {
                            setFilters({
                              ...filters,
                              [filterKey]: filters[filterKey].filter(value => value !== option.value)
                            });
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    }

                    <FontAwesomeIcon
                      className="category-icon"
                      icon={category.accordionIcons[index].icon}
                      style={{ margin: '0 10px' }}
                    />
                    {option.label}
                  </div>
                  {
                    childOption &&
                    <div className="dropdown-icon">
                      <FontAwesomeIcon className="arrow-icon" icon={faChevronDown}/>
                    </div>
                  }
                </Accordion.Header>
                {
                  childOption &&
                  <Accordion.Body>
                    {childOption.map((subclass) => {
                      return (
                        <div className="subclass-item" key={subclass.value}>
                          <Checkbox
                            checked={filters[childFilterKey].includes(subclass.value)}
                            key={subclass.value}
                            name={`${subclass.label}_check`}
                            onChange={(e) => {
                              const { checked } = e.target;
                              if (checked) {
                                setFilters({
                                  ...filters,
                                  [childFilterKey]: [...filters[childFilterKey], subclass.value]
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  [childFilterKey]: filters[childFilterKey].filter(value => value !== subclass.value)
                                });
                              }
                            }}
                          >
                            {subclass.label}
                          </Checkbox>
                        </div>
                      );
                    })}
                  </Accordion.Body>
                }
              </Accordion.Item>
            );
          })}
        </Accordion>
      </div>
    );
  };

  const validFilters = Object.keys(filterMapping).filter((filterKey) => {
    const filterType = filterMapping[filterKey].type;

    if (FILTER_TYPES_REQUIRING_OPTIONS.includes(filterType)) {
      return filterMapping[filterKey].options.length > 0;
    }

    return true;
  });

  const filterSection = (filterCategories) => {
    return (
      <React.Fragment>
        {filterCategories.map((category) => {
          const filters = validFilters.filter(key => filterMapping[key].category === category.label);

          return (
            <Col className="filter-category-group" key={category.label}>
              {category.label !== 'Search' && <h3 className="filter-category-headers">{category.label}</h3>}
              {filters.map(key => (
                <Row className="filter-option" key={key} sm={6}>
                  {filterMapping[key].showLabel !== false && (
                    <div className="d-flex align-items-center gap-3">
                      <h5>{filterMapping[key].label}</h5>
                      {filterMapping[key].childFilter ? renderChildFilter(key) : null}
                    </div>
                  )}

                  {filterMapping[key].type === 'search' && renderSearchFilter(key)}
                  {filterMapping[key].type === 'multiselect' && renderFilterBox(key)}
                  {filterMapping[key].type === 'range' && renderRangeBox(key, category)}
                  {filterMapping[key].type === 'date' && renderDateRangeBox(key, category)}
                  {filterMapping[key].type === 'dropdown' && renderDropdownFilterBox(key)}
                  {filterMapping[key].type === 'accordion' && renderAccordionFilterBox(key, category)}
                </Row>
              ))}
            </Col>
          );
        })}
      </React.Fragment>
    );
  };

  const mainFilters = () => {
    return (
      allFilterOrder.map((categories, index) => {
        return (
          <Col
            className={`filter-cols ${index === 0 ? 'first-filter-col' : ''}`}
            key={index}
            sm={4}
          >
            {filterSection(categories)}
          </Col>
        );
      })
    );
  };

  return (
    <Modal show={show} size="large" onHide={onCloseModal}>
      <Modal.Header closeButton>
        <div className="filter-modal-header">
          <FontAwesomeIcon className="mr-3 ds-text-secondary" icon={faSliders}/>
          All Filters
        </div>
      </Modal.Header>

      <Modal.Body className="filter-modal-body">
        <Row className="filter-body-container">
          {mainFilters()}
        </Row>

        <Row
          className="filter-action-buttons"
          style={{ borderTop: '1px solid #D8DADF', paddingTop: '1.5rem' }}
        >
          <Col sm={8}>
            <Button.Danger className="ds-button--plain gap-2" name="clearFilters" onClick={clearFilters}>
              <FontAwesomeIcon icon={faTimes}/> Clear All
            </Button.Danger>
          </Col>
          <Col className="text-right" sm={4}>
            <Button.Cancel className="mr-3" name="cancel" onClick={onCloseModal}>
              Cancel
            </Button.Cancel>
            <Button.Primary
              disabled={!filtersModified}
              name="saveFilters"
              onClick={saveFilters}
            >
              Apply Filters
            </Button.Primary>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

AllFiltersModal.propTypes = {
  allFilterOrder: PropTypes.array.isRequired,
  filterMapping: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  startingFilters: PropTypes.object.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};
